import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ChartComments } from './ChartComments';
import { supabaseClient } from '../../services/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useUserRole } from '../../contexts/UserRoleContext';
import { useChartPngRegistryActions, chartPngIdFromTitle, ChartPngPageIdOverrideContext, type ChartPngPageId } from '../../contexts/ChartPngRegistryContext';
import { CLIMATEPLANS_DATA_USE_NOTICE } from '../../constants/dataUsePolicy';
import { NARRATIVE_METHODOLOGY_TAB_LABEL } from '../../constants/narrativeTabLabels';
import { useMemberExportsLocked } from '../../hooks/useMemberExportsLocked';
import { MemberExportNotice } from '../membership/MemberExportNotice';
import './ChartLayout.css';

// Grouped Chart Selector Component
interface GroupedChartSelectorProps {
  groups: {
    category: string;
    label: string;
    options: { value: string; label: string }[];
  }[];
  value: string;
  onChange: (value: string) => void;
}

const GroupedChartSelector: React.FC<GroupedChartSelectorProps> = ({ groups, value, onChange }) => {
  // Find which category the current value belongs to
  const activeCategory = useMemo(() => {
    for (const group of groups) {
      if (group.options.some(opt => opt.value === value)) {
        return group.category;
      }
    }
    return groups[0]?.category || '';
  }, [groups, value]);

  const activeGroup = groups.find(g => g.category === activeCategory);

  return (
    <div className="grouped-chart-selector">
      {/* Main category buttons */}
      <div className="chart-selector-categories">
        {groups.map(group => (
          <button
            key={group.category}
            className={`category-btn ${activeCategory === group.category ? 'active' : ''}`}
            onClick={() => {
              // Switch to first option in this category when category is clicked
              if (activeCategory !== group.category && group.options.length > 0) {
                onChange(group.options[0].value);
              }
            }}
          >
            {group.label}
          </button>
        ))}
      </div>
      {/* Sub-option buttons for active category */}
      {activeGroup && (
        <div className="chart-selector-options">
          {activeGroup.options.map(option => (
            <button
              key={option.value}
              className={`toggle-btn ${value === option.value ? 'active' : ''}`}
              onClick={() => onChange(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

interface ChartLayoutProps {
  title: string;
  description?: string;
  chartType?: 'line' | 'bar' | 'doughnut' | 'pie' | 'map';
  keyMetrics?: ReactNode;
  chartContent: ReactNode;
  overviewContent?: ReactNode;
  relevanceContent?: ReactNode;
  equityContent?: ReactNode;
  methodologyContent?: ReactNode;
  activeView: string;
  onViewChange: (view: string) => void;
  chartSelector?: {
    options?: { value: string; label: string }[];
    value: string;
    onChange: (value: string) => void;
    grouped?: boolean;
    groups?: {
      category: string;
      label: string;
      options: { value: string; label: string }[];
    }[];
  };
  className?: string;
  showRelevanceEquity?: boolean;
  layout?: 'side-by-side' | 'vertical';
  // Download functionality
  onDownloadPNG?: () => void;
  onDownloadCSV?: () => void;
  pngFilename?: string;
  csvFilename?: string;
  // Data table functionality
  dataTableContent?: ReactNode;
  showDataTable?: boolean;
  onToggleDataTable?: () => void;
  // Comments functionality
  enableComments?: boolean;
  location?: string;
  overviewSectionId?: string;
  overviewChartId?: string;
  /** Optional id for the section element; used for deep-linking from livability/equity index */
  scrollAnchorId?: string;
  /** When false, this chart does not register for the header "Download PNGs" list (e.g. when the page registers each view separately). Default true. */
  registerPng?: boolean;
  /**
   * When true and `location` is set, the visible section `<h3>` is `{title} - {location}`.
   * The `title` prop (and DB `chart_title` overrides) remain the base title without the suffix.
   */
  appendLocationToTitle?: boolean;
  // Additional button groups (for backcasting options, etc.)
  additionalButtonGroups?: {
    label: string;
    options: { value: string; label: string }[];
    value: string;
    onChange: (value: string) => void;
  }[];
}

interface ChartTextOverridesContextValue {
  canEditText: boolean;
  location?: string;
  chartTitle?: string;
  overrides: Record<string, string>;
  editingKey: string | null;
  draftText: string;
  setDraftText: (value: string) => void;
  startEditing: (key: string, seedValue: string) => void;
  cancelEditing: () => void;
  saveOverride: (key: string) => void;
  removeOverride: (key: string) => void;
}

const ChartTextOverridesContext = React.createContext<ChartTextOverridesContextValue | null>(null);

const ChartControls: React.FC<{
  chartSelector?: ChartLayoutProps['chartSelector'];
  additionalButtonGroups?: ChartLayoutProps['additionalButtonGroups'];
}> = ({ chartSelector, additionalButtonGroups }) => (
  <>
    {chartSelector && (
      chartSelector.grouped && chartSelector.groups ? (
        <GroupedChartSelector groups={chartSelector.groups} value={chartSelector.value} onChange={chartSelector.onChange} />
      ) : chartSelector.options ? (
        <div className="chart-selector">
          {chartSelector.options.map(option => (
            <button key={option.value} className={`toggle-btn ${chartSelector.value === option.value ? 'active' : ''}`} onClick={() => chartSelector.onChange(option.value)}>{option.label}</button>
          ))}
        </div>
      ) : null
    )}
    {additionalButtonGroups && additionalButtonGroups.length > 0 && (
      <div className="additional-button-groups">
        {additionalButtonGroups.map((group, idx) => (
          <div key={idx} className="additional-button-group">
            <label className="additional-button-group-label">{group.label}:</label>
            <select className="additional-button-group-select" value={group.value} onChange={(e) => group.onChange(e.target.value)}>
              {group.options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </div>
        ))}
      </div>
    )}
  </>
);

const NarrativePanel: React.FC<{
  overviewContent?: ReactNode; relevanceContent?: ReactNode; equityContent?: ReactNode; methodologyContent?: ReactNode;
  activeView: string; onViewChange: (view: string) => void; showRelevanceEquity: boolean; wrapperClassName: string;
}> = ({ overviewContent, relevanceContent, equityContent, methodologyContent, activeView, onViewChange, showRelevanceEquity, wrapperClassName }) => {
  const hasContent = overviewContent || relevanceContent || equityContent || methodologyContent;
  if (!hasContent) return null;
  const tabs: { key: string; label: string; content: ReactNode; show: boolean }[] = [
    { key: 'overview', label: 'Overview', content: overviewContent, show: !!overviewContent },
    { key: 'relevance', label: 'Relevance', content: relevanceContent, show: showRelevanceEquity },
    { key: 'equity', label: 'Equity', content: equityContent, show: showRelevanceEquity },
    { key: 'methodology', label: NARRATIVE_METHODOLOGY_TAB_LABEL, content: methodologyContent, show: !!methodologyContent },
  ];
  return (
    <div className={wrapperClassName}>
      <div className="toggle-buttons">
        {tabs.filter(t => t.show).map(t => (
          <button key={t.key} className={`toggle-btn ${activeView === t.key ? 'active' : ''}`} onClick={() => onViewChange(t.key)}>{t.label}</button>
        ))}
      </div>
      <div className="content-sections">
        {tabs.filter(t => t.show).map(t => (
          <div
            key={t.key}
            className={`content-section content-section-${t.key} ${activeView === t.key ? '' : 'hidden'}`}
          >
            {t.content}
          </div>
        ))}
      </div>
    </div>
  );
};

const renderOverrideParagraphs = (text: string) => {
  const blocks = text.trim().split(/\n\s*\n/);
  return blocks.map((block, blockIndex) => {
    const lines = block.split('\n');
    return (
      <p key={`override-block-${blockIndex}`}>
        {lines.map((line, lineIndex) => (
          <React.Fragment key={`override-line-${blockIndex}-${lineIndex}`}>
            {line}
            {lineIndex < lines.length - 1 ? <br /> : null}
          </React.Fragment>
        ))}
      </p>
    );
  });
};

const htmlToPlainText = (value: string) =>
  value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .trim();

const extractPlainText = (node: ReactNode): string => {
  if (node === null || node === undefined || node === false) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) {
    return node
      .map(extractPlainText)
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
  if (React.isValidElement(node)) {
    return extractPlainText(node.props?.children);
  }
  return '';
};

const extractSimpleCardContent = (node: ReactNode): { title: string; body: string } => {
  let titleText = '';
  let bodyText = '';

  if (React.isValidElement(node)) {
    React.Children.forEach(node.props?.children, (child) => {
      if (!React.isValidElement(child)) return;
      const element = child as React.ReactElement<any>;
      if (element.type === 'h5' && !titleText) {
        titleText = extractPlainText(element.props?.children);
      }
      if (element.type === 'p' && !bodyText) {
        bodyText = extractPlainText(element.props?.children);
      }
    });
  }

  return { title: titleText, body: bodyText };
};

const isSimpleCardElement = (node: ReactNode): node is React.ReactElement => {
  if (!React.isValidElement(node)) return false;
  const className = node.props?.className;
  return typeof className === 'string' && className.includes('methodology-details-simple');
};

const EditableField: React.FC<{
  fieldKey: string; defaultValue: string; tag: 'h4' | 'h5' | 'p' | 'description';
  placeholder?: string; rows?: number; editLabel?: string;
}> = ({ fieldKey, defaultValue, tag, placeholder, rows = 4, editLabel = 'Edit' }) => {
  const ctx = React.useContext(ChartTextOverridesContext);
  const overrides = ctx?.overrides || {};
  const canEdit = !!ctx?.canEditText && !!ctx?.location;
  const override = overrides[fieldKey];
  const isEditing = ctx?.editingKey === fieldKey;

  if (isEditing) {
    return (
      <div className="editable-section-editor">
        <textarea className="editable-section-textarea" value={ctx?.draftText || ''} onChange={(e) => ctx?.setDraftText(e.target.value)} placeholder={placeholder} rows={rows} />
        <div className="editable-section-actions">
          <button className="edit-text-button" onClick={() => ctx?.saveOverride(fieldKey)}>Save</button>
          <button className="edit-text-secondary" onClick={ctx?.cancelEditing}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="editable-section">
      {canEdit && (
        <div className="editable-section-controls">
          <button className="edit-text-button" onClick={() => ctx?.startEditing(fieldKey, override ?? (tag === 'description' ? htmlToPlainText(defaultValue) : defaultValue))}>{editLabel}</button>
          {override && <button className="edit-text-secondary" onClick={() => ctx?.removeOverride(fieldKey)}>Restore default</button>}
        </div>
      )}
      {tag === 'h4' && <h4>{override || defaultValue}</h4>}
      {tag === 'h5' && <h5>{override || defaultValue}</h5>}
      {tag === 'p' && (override ? renderOverrideParagraphs(override) : <p>{defaultValue}</p>)}
      {tag === 'description' && (override
        ? <div className="editable-section-override">{renderOverrideParagraphs(override)}</div>
        : <p dangerouslySetInnerHTML={{ __html: defaultValue }} />
      )}
    </div>
  );
};

export const ChartLayout: React.FC<ChartLayoutProps> = ({
  title,
  description,
  chartType = 'line',
  keyMetrics,
  chartContent,
  overviewContent,
  relevanceContent,
  equityContent,
  methodologyContent,
  activeView,
  onViewChange,
  chartSelector,
  appendLocationToTitle = false,
  className = '',
  showRelevanceEquity = false,
  layout = 'side-by-side',
  onDownloadPNG,
  onDownloadCSV,
  pngFilename = 'Chart',
  csvFilename = 'Data',
  dataTableContent,
  showDataTable = false,
  onToggleDataTable,
  enableComments = false,
  location,
  scrollAnchorId,
  registerPng = true,
  additionalButtonGroups
}) => {
  const sectionHeading =
    appendLocationToTitle && location?.trim() ? `${title} - ${location.trim()}` : title;

  const { user } = useAuth();
  const { hasAnyRole } = useUserRole();
  // Internal state for managing comments toggle
  const [showComments, setShowComments] = useState(false);
  const [commentsCount, setCommentsCount] = useState(0);
  const [textOverrides, setTextOverrides] = useState<Record<string, string>>({});
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draftText, setDraftText] = useState('');

  const normalizeLocation = (value?: string | null) =>
    (value || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();

  const userLocation = normalizeLocation(user?.user_metadata?.specific_location);
  const chartLocation = normalizeLocation(location);
  const isOwnLocation = !!userLocation && !!chartLocation && userLocation === chartLocation;
  const canEditText = hasAnyRole(['local_govt_admin', 'super_admin']) && (hasAnyRole(['super_admin']) || isOwnLocation);

  const exportsLocked = useMemberExportsLocked();
  const hasExportControls = !!(onDownloadPNG || onDownloadCSV || onToggleDataTable);
  const showDownloadButtons = !exportsLocked && !!(onDownloadPNG || onDownloadCSV);
  const showDataTableToggle = !exportsLocked && !!onToggleDataTable;

  // Fetch comments count
  useEffect(() => {
    if (enableComments) {
      const fetchCount = async () => {
        const { count } = await supabaseClient
          .from('community_comments')
          .select('*', { count: 'exact', head: true })
          .eq('chart_context', title)
          .is('parent_id', null);
        setCommentsCount(count || 0);
      };
      fetchCount();
    }
  }, [enableComments, title]);

  const loadOverrides = useCallback(async () => {
    if (!location || !title) return;
    const { data, error } = await supabaseClient
      .from('chart_text_overrides')
      .select('section, content')
      .eq('location_name', location)
      .eq('chart_title', title);

    if (error) {
      console.error('Error fetching text overrides:', error);
      return;
    }

    const nextOverrides: Record<string, string> = {};
    (data || []).forEach((row: any) => {
      if (row.section && row.content) {
        nextOverrides[row.section] = row.content;
      }
    });
    setTextOverrides(nextOverrides);
  }, [location, title]);

  useEffect(() => {
    loadOverrides();
  }, [loadOverrides]);

  // Register with chart PNG registry so the download modal can list and trigger this chart's export (unless the page registers each view separately)
  const { register: registerPngChart, unregister: unregisterPngChart } = useChartPngRegistryActions();
  const chartId = chartPngIdFromTitle(title);
  const pageIdOverride = useContext(ChartPngPageIdOverrideContext);
  const routerLocation = useLocation();
  const downloadPageId = useMemo((): ChartPngPageId | undefined => {
    if (pageIdOverride) return pageIdOverride;
    const pathname = routerLocation?.pathname ?? '';
    const segments = pathname.split('/').filter(Boolean);
    const last = segments[segments.length - 1];
    if (last === 'other') return 'waste';
    if (last === 'ghg') return 'ghg';
    if (['transportation', 'buildings', 'people', 'overview'].includes(last)) return last as ChartPngPageId;
    return 'overview';
  }, [pageIdOverride, routerLocation?.pathname]);
  useEffect(() => {
    if (exportsLocked || !registerPng || !onDownloadPNG) return;
    registerPngChart(chartId, title, onDownloadPNG, downloadPageId);
    return () => unregisterPngChart(chartId);
  }, [exportsLocked, registerPng, chartId, title, onDownloadPNG, downloadPageId, registerPngChart, unregisterPngChart]);

  const startEditing = useCallback((key: string, seedValue: string) => {
    setEditingKey(key);
    setDraftText(textOverrides[key] ?? seedValue ?? '');
  }, [textOverrides]);

  const cancelEditing = useCallback(() => {
    setEditingKey(null);
    setDraftText('');
  }, []);

  const saveOverride = useCallback(async (key: string) => {
    if (!location || !title || !user) return;
    if (!draftText.trim()) return;

    const confirmSave = window.confirm(
      'This will permanently replace the generated narrative with your custom text. Continue?'
    );
    if (!confirmSave) return;

    const { error } = await supabaseClient
      .from('chart_text_overrides')
      .upsert({
        location_name: location,
        chart_title: title,
        section: key,
        content: draftText.trim(),
        updated_by: user.id,
        updated_at: new Date().toISOString()
      }, { onConflict: 'location_name,chart_title,section' });

    if (error) {
      console.error('Error saving text override:', error);
      return;
    }

    setTextOverrides((prev) => ({ ...prev, [key]: draftText.trim() }));
    setEditingKey(null);
    setDraftText('');
  }, [location, title, user, draftText]);

  const removeOverride = useCallback(async (key: string) => {
    if (!location || !title || !user) return;

    const confirmRemove = window.confirm('Remove the custom text and restore the default narrative?');
    if (!confirmRemove) return;

    const { error } = await supabaseClient
      .from('chart_text_overrides')
      .delete()
      .eq('location_name', location)
      .eq('chart_title', title)
      .eq('section', key);

    if (error) {
      console.error('Error removing text override:', error);
      return;
    }

    setTextOverrides((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, [location, title, user]);

  const contextValue = useMemo<ChartTextOverridesContextValue>(
    () => ({
      canEditText,
      location: location || undefined,
      chartTitle: title,
      overrides: textOverrides,
      editingKey,
      draftText,
      setDraftText,
      startEditing,
      cancelEditing,
      saveOverride,
      removeOverride
    }),
    [canEditText, location, title, textOverrides, editingKey, draftText, removeOverride, saveOverride, startEditing, cancelEditing]
  );

  // Handle custom data table toggle - close comments if open
  const handleToggleDataTable = () => {
    setShowComments(false);
    if (onToggleDataTable) {
      onToggleDataTable();
    }
  };

  // Handle comments toggle - close data table if open
  const handleToggleComments = () => {
    if (showDataTable && onToggleDataTable) {
      onToggleDataTable();
    }
    setShowComments(!showComments);
  };

  return (
    <ChartTextOverridesContext.Provider value={contextValue}>
      <div className={`chart-section ${className}`} id={scrollAnchorId || undefined}>
        <div className="chart-content">
        <div className="chart-title-section">
          <h3>{sectionHeading}</h3>
          {description && (
            <p 
              className="chart-description-text" 
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
          
          {/* Key Metrics - right after title */}
          {keyMetrics && (
            <div className="key-metrics-after-title">
              {keyMetrics}
            </div>
          )}
        </div>
        
        {/* Conditional Layout: Side-by-side or Vertical */}
        {layout === 'side-by-side' ? (
          <div className={`chart-content-layout ${!(overviewContent || relevanceContent || equityContent || methodologyContent) ? 'single-column' : ''}`}>
            <div className="chart-left-side">
              <ChartControls chartSelector={chartSelector} additionalButtonGroups={additionalButtonGroups} />
              <div className="chart-container">{chartContent}</div>
              {(hasExportControls || enableComments || exportsLocked) && (
                <div className="chart-buttons-row chart-left-buttons">
                  <div className="left-buttons-group">
                    {showDataTableToggle && (
                      <button className="customize-data-btn" onClick={handleToggleDataTable} title="Customize Data">
                        {showDataTable ? 'Hide Data Table' : 'Customize Data'}
                      </button>
                    )}
                    {enableComments && (
                      <button className="comments-toggle-btn" onClick={handleToggleComments} title="View Comments">
                        💬 Comments {commentsCount > 0 && `(${commentsCount})`}
                      </button>
                    )}
                  </div>
                  {showDownloadButtons && (
                    <div className="download-buttons-group">
                      {onDownloadPNG && <button className="chart-download-btn" onClick={onDownloadPNG} title={`Download ${pngFilename} as PNG`}>🖼️ PNG</button>}
                      {onDownloadCSV && <button className="chart-download-btn" onClick={onDownloadCSV} title={`Download ${csvFilename} as CSV`}>📊 CSV</button>}
                    </div>
                  )}
                  {exportsLocked && (
                    <MemberExportNotice variant="compact" className="chart-export-lock-notice" />
                  )}
                </div>
              )}
              {exportsLocked && (
                <p className="chart-data-use-notice">{CLIMATEPLANS_DATA_USE_NOTICE}</p>
              )}
              {showDataTable && <div className="data-table-wrapper">{dataTableContent}</div>}
              {showComments && <div className="comments-wrapper"><ChartComments chartName={title} location={location} visible={showComments} showToggle={false} /></div>}
            </div>
            <NarrativePanel overviewContent={overviewContent} relevanceContent={relevanceContent} equityContent={equityContent} methodologyContent={methodologyContent} activeView={activeView} onViewChange={onViewChange} showRelevanceEquity={showRelevanceEquity} wrapperClassName="chart-right-side" />
          </div>
        ) : (
          <div className="chart-vertical-layout">
            <div className="chart-section">
              <ChartControls chartSelector={chartSelector} additionalButtonGroups={additionalButtonGroups} />
              <div className="chart-container">{chartContent}</div>
              {exportsLocked && (
                <>
                  <MemberExportNotice variant="compact" className="chart-export-lock-notice" />
                  <p className="chart-data-use-notice">{CLIMATEPLANS_DATA_USE_NOTICE}</p>
                </>
              )}
            </div>
            <NarrativePanel overviewContent={overviewContent} relevanceContent={relevanceContent} equityContent={equityContent} methodologyContent={methodologyContent} activeView={activeView} onViewChange={onViewChange} showRelevanceEquity={showRelevanceEquity} wrapperClassName="narrative-section" />
          </div>
        )}
      </div>
    </div>
    </ChartTextOverridesContext.Provider>
  );
};

// Content components for consistent structure
export const OverviewContent: React.FC<{
  title: string;
  description: string;
  children?: ReactNode;
}> = ({ title, description, children }) => {
  const ctx = React.useContext(ChartTextOverridesContext);
  const overrides = ctx?.overrides || {};
  const insightsOverride = overrides.overview_insights;
  const isEditingInsights = ctx?.editingKey === 'overview_insights';

  return (
    <div className="chart-narrative-overview">
      <EditableField fieldKey="overview_title" defaultValue={title} tag="h4" placeholder="Enter custom title..." rows={2} editLabel="Edit title" />
      <EditableField fieldKey="overview_description" defaultValue={description} tag="description" placeholder="Enter custom overview text..." rows={6} editLabel="Edit overview text" />
      {children && (
        <div className="key-metrics">
          <div className="editable-section">
            {!!ctx?.canEditText && !!ctx?.location && !isEditingInsights && (
              <div className="editable-section-controls">
                <button className="edit-text-button" onClick={() => ctx?.startEditing('overview_insights', insightsOverride ?? extractPlainText(children))}>Edit key insights</button>
                {insightsOverride && <button className="edit-text-secondary" onClick={() => ctx?.removeOverride('overview_insights')}>Restore default</button>}
              </div>
            )}
          </div>
          <h5>Key Insights</h5>
          <div className="insights-content">
            {isEditingInsights ? (
              <div className="editable-section-editor">
                <textarea className="editable-section-textarea" value={ctx?.draftText || ''} onChange={(e) => ctx?.setDraftText(e.target.value)} placeholder="Enter custom key insights..." rows={6} />
                <div className="editable-section-actions">
                  <button className="edit-text-button" onClick={() => ctx?.saveOverride('overview_insights')}>Save</button>
                  <button className="edit-text-secondary" onClick={ctx?.cancelEditing}>Cancel</button>
                </div>
              </div>
            ) : insightsOverride ? renderOverrideParagraphs(insightsOverride) : children}
          </div>
        </div>
      )}
    </div>
  );
};

const EditableNarrativeSection: React.FC<{
  sectionPrefix: string;
  title: string;
  description: string;
  children?: ReactNode;
}> = ({ sectionPrefix, title, description, children }) => {
  const ctx = React.useContext(ChartTextOverridesContext);
  const overrides = ctx?.overrides || {};
  const canEdit = !!ctx?.canEditText && !!ctx?.location;
  const titleKey = `${sectionPrefix}_title`;
  const descKey = `${sectionPrefix}_description`;
  const titleOverride = overrides[titleKey];
  const descriptionOverride = overrides[descKey];
  const isEditingTitle = ctx?.editingKey === titleKey;
  const isEditingDescription = ctx?.editingKey === descKey;
  let cardIndex = 0;

  const renderEditableCard = (cardTitle: string, cardBody: string) => {
    const index = cardIndex;
    cardIndex += 1;
    const cTitleKey = `${sectionPrefix}_card_${index}_title`;
    const cBodyKey = `${sectionPrefix}_card_${index}_body`;
    const cTitleOverride = overrides[cTitleKey];
    const cBodyOverride = overrides[cBodyKey];
    const isEditingCTitle = ctx?.editingKey === cTitleKey;
    const isEditingCBody = ctx?.editingKey === cBodyKey;
    return (
      <div className="methodology-details-simple" key={cTitleKey}>
        {canEdit && !isEditingCTitle && !isEditingCBody && (
          <div className="editable-section-controls">
            <button className="edit-text-button" onClick={() => ctx?.startEditing(cTitleKey, cTitleOverride ?? cardTitle)}>Edit card title</button>
            <button className="edit-text-button" onClick={() => ctx?.startEditing(cBodyKey, cBodyOverride ?? cardBody)}>Edit card text</button>
            {(cTitleOverride || cBodyOverride) && (
              <button className="edit-text-secondary" onClick={() => { if (cTitleOverride) ctx?.removeOverride(cTitleKey); if (cBodyOverride) ctx?.removeOverride(cBodyKey); }}>Restore default</button>
            )}
          </div>
        )}
        <EditableField fieldKey={cTitleKey} defaultValue={cardTitle} tag="h5" placeholder="Enter card title..." rows={2} />
        <EditableField fieldKey={cBodyKey} defaultValue={cardBody} tag="p" placeholder="Enter card text..." rows={4} />
      </div>
    );
  };

  return (
    <>
      <EditableField fieldKey={titleKey} defaultValue={title} tag="h4" placeholder="Enter custom title..." rows={2} editLabel="Edit title" />
      <EditableField fieldKey={descKey} defaultValue={description} tag="description" placeholder={`Enter custom ${sectionPrefix} text...`} rows={6} editLabel={`Edit ${sectionPrefix} text`} />
      {React.Children.map(children, (child) => {
        if (!isSimpleCardElement(child)) return child;
        const { title: cardTitle, body: cardBody } = extractSimpleCardContent(child);
        return renderEditableCard(cardTitle, cardBody);
      })}
    </>
  );
};

export const RelevanceContent: React.FC<{ title: string; description: string; children?: ReactNode }> = (props) => (
  <EditableNarrativeSection sectionPrefix="relevance" {...props} />
);

export const EquityContent: React.FC<{ title: string; description: string; children?: ReactNode }> = (props) => (
  <EditableNarrativeSection sectionPrefix="equity" {...props} />
);

const EditableListField: React.FC<{
  fieldKey: string; defaultItems: string[]; placeholder: string; editLabel: string;
}> = ({ fieldKey, defaultItems, placeholder, editLabel }) => {
  const ctx = React.useContext(ChartTextOverridesContext);
  const overrides = ctx?.overrides || {};
  const canEdit = !!ctx?.canEditText && !!ctx?.location;
  const override = overrides[fieldKey];
  const isEditing = ctx?.editingKey === fieldKey;
  const items = override ? override.split('\n').map(s => s.trim()).filter(Boolean) : defaultItems;

  return (
    <>
      {canEdit && !isEditing && (
        <div className="editable-section">
          <div className="editable-section-controls">
            <button className="edit-text-button" onClick={() => ctx?.startEditing(fieldKey, override ?? defaultItems.join('\n'))}>{editLabel}</button>
            {override && <button className="edit-text-secondary" onClick={() => ctx?.removeOverride(fieldKey)}>Restore default</button>}
          </div>
        </div>
      )}
      {isEditing && (
        <div className="editable-section-editor">
          <textarea className="editable-section-textarea" value={ctx?.draftText || ''} onChange={(e) => ctx?.setDraftText(e.target.value)} placeholder={placeholder} rows={6} />
          <div className="editable-section-actions">
            <button className="edit-text-button" onClick={() => ctx?.saveOverride(fieldKey)}>Save</button>
            <button className="edit-text-secondary" onClick={ctx?.cancelEditing}>Cancel</button>
          </div>
        </div>
      )}
      <ul>{items.map((item, i) => <li key={i} dangerouslySetInnerHTML={{ __html: item }} />)}</ul>
    </>
  );
};

export const MethodologyContent: React.FC<{
  title: string;
  description: string;
  dataSources: string[];
  methodology: string[];
}> = ({ title, description, dataSources, methodology }) => (
  <>
    <EditableField fieldKey="methodology_title" defaultValue={title} tag="h4" placeholder="Enter custom title..." rows={2} editLabel="Edit title" />
    <EditableField fieldKey="methodology_description" defaultValue={description} tag="description" placeholder="Enter custom methodology text..." rows={6} editLabel="Edit methodology text" />
    <div className="methodology-details">
      <h5>Data Sources</h5>
      <EditableListField fieldKey="methodology_sources" defaultItems={dataSources} placeholder="Enter one data source per line..." editLabel="Edit data sources" />
      <h5>Methodology</h5>
      <EditableListField fieldKey="methodology_steps" defaultItems={methodology} placeholder="Enter one methodology step per line..." editLabel="Edit methodology steps" />
    </div>
  </>
);
